import { useCallback, useMemo, useState } from "react";
import { useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { supabase } from "@/integrations/supabase/client";
import {
  buildCoachEvidenceView,
  buildCoachTranscriptReviewView,
  getCoachReviewArtifactKey,
  type CoachReviewReferenceInput,
  type CoachReviewResolvedSource,
} from "@/lib/coachReviewArtifacts";

type CoachReviewArtifactViewRow = {
  artifact_key: string;
};

function mergeArtifactRows(
  currentRows: CoachReviewArtifactViewRow[] | undefined,
  artifactKeys: string[],
): CoachReviewArtifactViewRow[] {
  const mergedRows = new Map<string, CoachReviewArtifactViewRow>();
  (currentRows ?? []).forEach((row) => {
    mergedRows.set(row.artifact_key, row);
  });
  artifactKeys.forEach((artifactKey) => {
    mergedRows.set(artifactKey, { artifact_key: artifactKey });
  });
  return Array.from(mergedRows.values());
}

type UseCoachReviewArtifactViewsParams = {
  coachId?: string | null;
  leaderId?: string | null;
  enabled?: boolean;
};

type MarkTranscriptReviewOpenedInput = {
  transcriptId: string;
  openedFrom: string;
  extraInvalidationKeys?: QueryKey[];
  requireReadback?: boolean;
};

type MarkEvidenceReferenceViewedInput = {
  reference: CoachReviewReferenceInput;
  resolvedSource?: CoachReviewResolvedSource | null;
  openedFrom: string;
  extraInvalidationKeys?: QueryKey[];
};

type MarkAllReviewedInput = {
  transcriptIds: string[];
  evidenceReferences: Array<CoachReviewReferenceInput & {
    resolvedSource?: CoachReviewResolvedSource | null;
  }>;
  openedFrom: string;
  extraInvalidationKeys?: QueryKey[];
};

export function useCoachReviewArtifactViews(params: UseCoachReviewArtifactViewsParams) {
  const { coachId, leaderId, enabled = true } = params;
  const queryClient = useQueryClient();
  const [optimisticViewedArtifactKeys, setOptimisticViewedArtifactKeys] = useState<Set<string>>(new Set());
  const queryKey = useMemo(
    () => ["coach-review-artifact-views", coachId, leaderId] as const,
    [coachId, leaderId],
  );
  const queryEnabled = Boolean(enabled && coachId && leaderId);

  const { data: viewedArtifactRows, isFetched } = useQuery({
    queryKey,
    enabled: queryEnabled,
    queryFn: async () => {
      if (!coachId || !leaderId) return [] as CoachReviewArtifactViewRow[];
      const { data, error } = await supabase
        .from("coach_review_artifact_views")
        .select("artifact_key")
        .eq("coach_id", coachId)
        .eq("leader_id", leaderId)
        .in("artifact_type", ["evidence_reference", "transcript_review"]);
      if (error) {
        console.error("Failed to read coach evidence review state:", error);
        return [] as CoachReviewArtifactViewRow[];
      }
      return data || [];
    },
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });

  const normalizedViewedArtifactRows = useMemo(
    () => viewedArtifactRows ?? [],
    [viewedArtifactRows],
  );
  const viewedArtifactKeys = useMemo(() => {
    const merged = new Set<string>(normalizedViewedArtifactRows.map((row) => row.artifact_key));
    optimisticViewedArtifactKeys.forEach((key) => merged.add(key));
    return merged;
  }, [optimisticViewedArtifactKeys, normalizedViewedArtifactRows]);

  const persistArtifactView = useCallback(async (
    artifactRecord: {
      artifact_key: string;
      artifact_type: "transcript_review" | "evidence_reference";
      coach_id: string;
      leader_id: string;
      transcript_upload_id: string | null;
      meeting_id: string | null;
      artifact_label: string | null;
      artifact_link: string | null;
      opened_from: string;
    },
    extraInvalidationKeys: QueryKey[] = [],
    requireReadback = false,
  ) => {
    const optimisticArtifactKey = artifactRecord.artifact_key;
    const previousRows = queryClient.getQueryData<CoachReviewArtifactViewRow[]>(queryKey);
    setOptimisticViewedArtifactKeys((current) => {
      const next = new Set(current);
      next.add(optimisticArtifactKey);
      return next;
    });
    queryClient.setQueryData<CoachReviewArtifactViewRow[]>(
      queryKey,
      (currentRows) => mergeArtifactRows(currentRows, [optimisticArtifactKey]),
    );

    const { error: writeError } = await supabase
      .from("coach_review_artifact_views")
      .upsert(
        {
          ...artifactRecord,
          last_viewed_at: new Date().toISOString(),
        },
        { onConflict: "coach_id,artifact_type,artifact_key" },
      );

    let persistenceError = writeError;
    if (!persistenceError && requireReadback) {
      const { data: persistedRow, error: readbackError } = await supabase
        .from("coach_review_artifact_views")
        .select("artifact_key")
        .eq("coach_id", artifactRecord.coach_id)
        .eq("leader_id", artifactRecord.leader_id)
        .eq("artifact_type", artifactRecord.artifact_type)
        .eq("artifact_key", artifactRecord.artifact_key)
        .eq("opened_from", artifactRecord.opened_from)
        .maybeSingle();
      if (readbackError || !persistedRow) {
        persistenceError = readbackError ?? { message: "Coach review artifact readback was empty" };
      }
    }

    if (persistenceError) {
      queryClient.setQueryData(queryKey, previousRows ?? []);
      setOptimisticViewedArtifactKeys((current) => {
        const next = new Set(current);
        next.delete(optimisticArtifactKey);
        return next;
      });
      console.error("Failed to persist coach review artifact view:", persistenceError);
      return false;
    }

    await queryClient.invalidateQueries({ queryKey });
    await queryClient.invalidateQueries({ queryKey: ["coach-pending-transcript-review-count", coachId] });
    await queryClient.invalidateQueries({ queryKey: ["coach-leaders-command"] });
    await Promise.all(
      extraInvalidationKeys.map((nextKey) => queryClient.invalidateQueries({ queryKey: nextKey })),
    );
    return true;
  }, [coachId, queryClient, queryKey]);

  const persistArtifactViews = useCallback(async (
    artifactRecords: Array<{
      artifact_key: string;
      artifact_type: "transcript_review" | "evidence_reference";
      coach_id: string;
      leader_id: string;
      transcript_upload_id: string | null;
      meeting_id: string | null;
      artifact_label: string | null;
      artifact_link: string | null;
      opened_from: string;
    }>,
    extraInvalidationKeys: QueryKey[] = [],
  ) => {
    if (artifactRecords.length === 0) return true;
    const optimisticArtifactKeys = artifactRecords.map((record) => record.artifact_key);
    const previousRows = queryClient.getQueryData<CoachReviewArtifactViewRow[]>(queryKey);

    setOptimisticViewedArtifactKeys((current) => {
      const next = new Set(current);
      optimisticArtifactKeys.forEach((artifactKey) => next.add(artifactKey));
      return next;
    });
    queryClient.setQueryData<CoachReviewArtifactViewRow[]>(
      queryKey,
      (currentRows) => mergeArtifactRows(currentRows, optimisticArtifactKeys),
    );

    const { error } = await supabase
      .from("coach_review_artifact_views")
      .upsert(
        artifactRecords.map((record) => ({
          ...record,
          last_viewed_at: new Date().toISOString(),
        })),
        { onConflict: "coach_id,artifact_type,artifact_key" },
      );

    if (error) {
      queryClient.setQueryData(queryKey, previousRows ?? []);
      setOptimisticViewedArtifactKeys((current) => {
        const next = new Set(current);
        optimisticArtifactKeys.forEach((artifactKey) => next.delete(artifactKey));
        return next;
      });
      console.error("Failed to persist coach review artifact views:", error);
      return false;
    }

    await queryClient.invalidateQueries({ queryKey });
    await queryClient.invalidateQueries({ queryKey: ["coach-pending-transcript-review-count", coachId] });
    await queryClient.invalidateQueries({ queryKey: ["coach-leaders-command"] });
    await Promise.all(
      extraInvalidationKeys.map((nextKey) => queryClient.invalidateQueries({ queryKey: nextKey })),
    );
    return true;
  }, [coachId, queryClient, queryKey]);

  const markTranscriptReviewOpened = useCallback(async ({
    transcriptId,
    openedFrom,
    extraInvalidationKeys = [],
    requireReadback = false,
  }: MarkTranscriptReviewOpenedInput) => {
    if (!coachId || !leaderId) return false;
    return persistArtifactView(
      buildCoachTranscriptReviewView({
        coachId,
        leaderId,
        transcriptId,
        openedFrom,
      }),
      extraInvalidationKeys,
      requireReadback,
    );
  }, [coachId, leaderId, persistArtifactView]);

  const markEvidenceReferenceViewed = useCallback(async ({
    reference,
    resolvedSource,
    openedFrom,
    extraInvalidationKeys = [],
  }: MarkEvidenceReferenceViewedInput) => {
    if (!coachId || !leaderId) return false;
    return persistArtifactView(
      buildCoachEvidenceView({
        coachId,
        leaderId,
        openedFrom,
        reference,
        resolvedSource,
      }),
      extraInvalidationKeys,
    );
  }, [coachId, leaderId, persistArtifactView]);

  const markAllReviewed = useCallback(async ({
    transcriptIds,
    evidenceReferences,
    openedFrom,
    extraInvalidationKeys = [],
  }: MarkAllReviewedInput) => {
    if (!coachId || !leaderId) return false;

    const transcriptRecords = Array.from(new Set(transcriptIds))
      .filter(Boolean)
      .map((transcriptId) => buildCoachTranscriptReviewView({
        coachId,
        leaderId,
        transcriptId,
        openedFrom,
      }));

    const evidenceRecordMap = new Map<string, ReturnType<typeof buildCoachEvidenceView>>();
    evidenceReferences.forEach((reference) => {
      const nextResolvedSource = reference.resolvedSource ?? null;
      evidenceRecordMap.set(
        getCoachReviewArtifactKey({
          ...reference,
          resolvedSource: nextResolvedSource,
        }),
        buildCoachEvidenceView({
          coachId,
          leaderId,
          openedFrom,
          reference,
          resolvedSource: nextResolvedSource,
        }),
      );
    });

    return persistArtifactViews(
      [...transcriptRecords, ...Array.from(evidenceRecordMap.values())],
      extraInvalidationKeys,
    );
  }, [coachId, leaderId, persistArtifactViews]);

  return {
    viewedArtifactKeys,
    isReady: !queryEnabled || isFetched,
    markTranscriptReviewOpened,
    markEvidenceReferenceViewed,
    markAllReviewed,
  };
}
